import { firstNameOf } from '@/lib/name'
import { labelsFor, listLabels } from '@/lib/careers/screening'

// Inline Vapi assistant for the Kingdom Kode careers first-round voice
// screen. Versioned here (not in the Vapi dashboard) so the interviewer
// persona stays in code review. The browser starts calls with
// NEXT_PUBLIC_VAPI_PUBLIC_KEY; transcripts sync into the application via
// /api/careers/interview/voice-sync, and OUR Claude agent does the
// structured extraction at call end — Vapi only owns the live voice
// conversation. Mirrors zero-to-hero/apply/vapi-assistant.js.

export const VOICE_OPENER =
  "Hi — thanks for making the time. This will run about ten minutes. Let's start simple: tell me a bit about yourself, and what drew you to this role."

// `remaining` (screening dimensions still uncovered) + `resuming` turn a
// returning candidate's call into a pickup, not a restart: the greeting
// names what's left and the system prompt forbids re-covering ground
// that's already answered. labelsFor/listLabels come from screening.ts —
// the single source of truth (mirrored no longer, previously duplicated
// here and in ScreenChat.jsx).

export function buildScreenAssistant({
  candidateName,
  postingTitle,
  interviewFocus,
  salaryRange = '',
  remaining = [],
  resuming = false,
}) {
  const first = firstNameOf(candidateName) || ''
  const resumeTarget = resuming && remaining.length > 0 ? labelsFor(remaining)[0] : null
  const firstMessage = resumeTarget
    ? `Welcome back${first ? `, ${first}` : ''} — good to have you again. Everything you've shared is saved, so let's pick up right where we left off: ${resumeTarget}. Ready?`
    : first
      ? `Hi ${first} — thanks for making the time. This will run about ten minutes. Let's start simple: tell me a bit about yourself, and what drew you to this role.`
      : VOICE_OPENER
  return {
    name: 'Kingdom Kode Careers Screener',
    firstMessage,
    // Vapi-hosted voice — works on every account without extra provider keys.
    // Swap for an ElevenLabs voiceId once you pick a brand voice.
    // fillerInjectionEnabled: false keeps the delivery polished — no "uh"/"um"
    // inserted for fake naturalness.
    voice: {
      provider: 'vapi',
      voiceId: 'Elliot',
      fillerInjectionEnabled: false,
    },
    transcriber: { provider: 'deepgram', model: 'nova-2', language: 'en' },
    model: {
      // gpt-4o is Vapi's most battle-tested realtime model; switch to
      // { provider: 'anthropic', model: '<current claude id>' } if preferred.
      provider: 'openai',
      model: 'gpt-4o',
      temperature: 0.6,
      messages: [
        {
          role: 'system',
          content: `You are the voice interviewer for Kingdom Kode's hiring team, running the first-round phone screen${postingTitle ? ` for the ${postingTitle} role` : ''}. This is a warm, professional screen — about 10 minutes — to understand whether the candidate is worth advancing to a hiring-manager round. You are not the hiring manager and you never decide the outcome.${candidateName ? ` You are speaking with ${candidateName}.` : ''}${salaryRange ? ` The salary range for this role is ${salaryRange}.` : ''}${
            resuming && remaining.length > 0
              ? `

RESUMING A PRIOR INTERVIEW: earlier answers are already saved. The ONLY areas still needing evidence are: ${listLabels(remaining)}. Do not re-introduce the role or re-ask what's already covered — go straight to the first missing area and work through only what's listed.`
              : ''
          }

You are having a natural spoken conversation. Rules:
- Warm, sharp, professional — a partner, never an interrogator. Before each question, acknowledge what they just said in your own words — the specific fact or detail they gave you. Let them feel heard before you move on.
- ONE question at a time. Keep each turn under 55 spoken words — acknowledgment and question together.
- Priority topics for this role (INTERVIEW FOCUS) come first: ${interviewFocus || '(none specified — work through the standard screening dimensions)'}. After those, round out any of these still missing evidence: role fit, experience depth, communication, ownership signals, logistics.
- logistics covers exactly three things: availability or start date, location or remote-work fit, and — only if a salary range was actually shared with you for this role — comp expectations. If no salary range was given, never ask about pay.
- Apply the Mom Test to vague answers. Compliments ("I'm a great fit"), hypotheticals ("I'd definitely handle that well"), and unquantified claims ("I have a lot of experience") are not evidence. When you hear one, your next question anchors to past behavior or real specifics: "Walk me through the last time you did that." "What exactly was your role — what did you personally build or decide?" "What was the outcome?" One warm redirect per vague answer; if it stays vague, note it and move on rather than interrogating.
- Silence is thinking, not completion. If the candidate pauses mid-thought or trails off, wait for them. If a half-finished thought comes through, invite them to finish it ("Take your time — you were saying?") instead of moving to a new question.
- Never promise an outcome, a next round, or an offer, and never evaluate the candidate out loud — no "that's a great answer" or "you're a strong fit," just genuine acknowledgment of what they said. No legal or compensation guarantees.
- Don't rush to close. Only end the call once the priority focus topics and the screening dimensions have real, specific evidence — not a single passing mention — or the candidate has clearly said they have nothing more after you've asked directly. When you do close, thank them by name if you have it, acknowledge something specific that stood out from the conversation, invite any questions they have about the role or Kingdom Kode, and tell them the team will review and follow up by email, and only then end the call.

Speaking style — this is a professional hiring process and your delivery must sound like it:
- Never say filler words: no "uh", "um", "you know", "like", "so" as an opener.
- Short, complete sentences with varied rhythm. Prefer a period over a comma splice.
- Use "and" sparingly — at most once per turn. Never chain clauses with "and... and...". Recast lists as separate sentences or a colon.
- Confident, precise, warm, unhurried. Excellence over chattiness — never cold.`,
        },
      ],
    },
    // Turn-taking: don't jump in while the candidate is mid-thought. LiveKit
    // smart endpointing (Vapi's recommendation for English) models whether the
    // sentence sounds finished; the transcription plan is the fallback — an
    // unpunctuated trail-off gets a long grace window before the interviewer
    // treats the turn as done.
    startSpeakingPlan: {
      waitSeconds: 0.8,
      smartEndpointingPlan: { provider: 'livekit' },
      transcriptionEndpointingPlan: {
        onPunctuationSeconds: 0.5,
        onNoPunctuationSeconds: 2.5,
        onNumberSeconds: 0.8,
      },
    },
    // Brief interjections ("mm-hm") shouldn't cut the interviewer off; two or
    // more words, or sustained speech, interrupt promptly.
    stopSpeakingPlan: { numWords: 2, voiceSeconds: 0.3, backoffSeconds: 1.0 },
    // Let the assistant close the interview itself when it's done.
    endCallFunctionEnabled: true,
    maxDurationSeconds: 1200,
  }
}
